import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, DownloadSimple, Warning, Paperclip, FilePdf } from "@phosphor-icons/react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TYPE_LABEL = {
  addition: "Tillägg",
  change: "Ändring",
  deduction: "Avgående",
  delay: "Hinder / väntetid",
};
const CATEGORY_LABEL = {
  labor: "Arbete",
  material: "Material",
  markup: "Påslag",
  other: "Övrigt",
};

function fmtKr(n) {
  return `${Number(n || 0).toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
}

/**
 * Publik signeringssida för ÄTA. Ingen inloggning krävs.
 * Beställaren når hit via mejllänk `/ata-signering/:token`.
 */
export default function AtaSigningPage() {
  const { token } = useParams();
  const [ata, setAta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null); // "approved" | "rejected"
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/ata/public/${token}`)
      .then((r) => { if (!cancelled) setAta(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || "Länken är ogiltig"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    axios.get(`${API_BASE}/ata/public/${token}/files`)
      .then((r) => { if (!cancelled) setAttachments(r.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const submitDecision = async (choice) => {
    if (choice === "rejected" && !rejectReason.trim()) {
      toast.error("Ange skäl vid nekande");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/ata/public/decision`, {
        token,
        decision: choice,
        reason: choice === "rejected" ? rejectReason.trim() : null,
      });
      // Uppdatera lokalt genom att hämta om
      const r = await axios.get(`${API_BASE}/ata/public/${token}`);
      setAta(r.data);
      setDecision(choice);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte skicka beslut");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f2ed] flex items-center justify-center text-text-muted">
        Laddar ÄTA-information…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f2ed] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <Warning size={48} className="mx-auto text-rust mb-4" />
          <h1 className="font-heading text-2xl mb-2">Länken är ogiltig</h1>
          <p className="text-text-muted text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const isDecided = ata.status === "approved" || ata.status === "rejected";

  return (
    <div className="min-h-screen bg-[#f5f2ed] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white border border-app rounded-2xl overflow-hidden shadow-sm">
          <div className="bg-[#1a2e25] text-[#f5f2ed] p-6">
            <div className="text-xs uppercase tracking-wider opacity-70">
              {ata.organization?.name} · {TYPE_LABEL[ata.ata_type]}
            </div>
            <h1 className="font-heading text-3xl mt-1">{ata.ata_number} · {ata.title}</h1>
            <div className="text-xs mt-1 opacity-70">
              Objekt: {ata.object?.name}{ata.object?.address ? ` · ${ata.object.address}` : ""}
            </div>
          </div>

          <div className="p-6 space-y-5 text-sm">
            {ata.description && (
              <section>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-1">Beskrivning</div>
                <p className="whitespace-pre-wrap">{ata.description}</p>
              </section>
            )}
            {ata.reason && (
              <section>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-1">Beställare / orsak</div>
                <p className="whitespace-pre-wrap">{ata.reason}</p>
              </section>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-1">Beställare enligt ÄTA</div>
                <div>{ata.beställare_company || "—"}</div>
                {ata.beställare_name && <div className="text-xs">{ata.beställare_name}</div>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-1">Tidspåverkan</div>
                {ata.affects_completion ? (
                  <div className="text-[var(--brand-rust)]">
                    Förlänger med {ata.completion_delay_days} dagar
                  </div>
                ) : (
                  <div className="text-text-muted">Påverkar ej slutdatum</div>
                )}
                {ata.contract_reference && (
                  <div className="text-xs text-text-muted mt-1">Ref: {ata.contract_reference}</div>
                )}
              </div>
            </div>

            {/* Line items */}
            <section>
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Specifikation</div>
              <div className="border border-app rounded-md overflow-hidden">
                {(ata.line_items || []).map((li, idx) => (
                  <div key={idx} className="grid grid-cols-12 px-3 py-2 border-b border-app last:border-0 text-xs items-center">
                    <div className="col-span-2 text-text-muted">{CATEGORY_LABEL[li.category]}</div>
                    <div className="col-span-5">{li.description}</div>
                    <div className="col-span-2 text-right font-mono">{li.quantity} {li.unit}</div>
                    <div className="col-span-3 text-right font-mono">{fmtKr(li.quantity * li.unit_price)}</div>
                  </div>
                ))}
                <div className="grid grid-cols-12 px-3 py-3 items-center text-sm bg-[#f5f2ed]">
                  <div className="col-span-9 font-medium">Totalt exkl. moms</div>
                  <div className="col-span-3 text-right font-mono font-bold text-base">
                    {fmtKr(ata.totals?.total_excl_vat)}
                  </div>
                </div>
              </div>
            </section>

            {/* Bilagor (foton och PDF) */}
            {attachments.length > 0 && (
              <section data-testid="signing-attachments">
                <div className="text-xs uppercase tracking-wider text-text-muted mb-2 flex items-center gap-1">
                  <Paperclip size={13} /> Bilagor ({attachments.length})
                </div>
                {attachments.filter((f) => f.is_image).length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                    {attachments.filter((f) => f.is_image).map((f) => (
                      <a
                        key={f.file_id}
                        href={`${API_BASE}/ata/public/${token}/files/${f.file_id}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`signing-image-${f.file_id}`}
                        className="block aspect-square rounded-md overflow-hidden border border-app bg-black/5 hover:opacity-90"
                      >
                        <img
                          src={`${API_BASE}/ata/public/${token}/files/${f.file_id}`}
                          alt={f.original_name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                )}
                {attachments.filter((f) => !f.is_image).map((f) => (
                  <a
                    key={f.file_id}
                    href={`${API_BASE}/ata/public/${token}/files/${f.file_id}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`signing-file-${f.file_id}`}
                    className="flex items-center gap-2 border border-app rounded-md px-3 py-2 mb-2 hover:bg-black/[0.03]"
                  >
                    <FilePdf size={18} className="text-rust shrink-0" />
                    <span className="flex-1 text-sm truncate">{f.original_name}</span>
                    <span className="text-xs text-text-muted">
                      {Math.round((f.size || 0) / 1024)} kB
                    </span>
                    <DownloadSimple size={16} className="text-text-muted" />
                  </a>
                ))}
              </section>
            )}

            {/* Download PDF */}
            <a
              href={`${API_BASE}/ata/public/${token}/pdf`}
              target="_blank" rel="noopener noreferrer"
              className="tk-btn-outline inline-flex items-center gap-2 text-xs"
              data-testid="download-signing-pdf"
            >
              <DownloadSimple size={14} /> Ladda ner som PDF
            </a>
          </div>
        </div>

        {/* Decision panel */}
        {isDecided ? (
          <div className={`mt-6 rounded-2xl p-6 text-center ${
            ata.status === "approved" ? "bg-[var(--brand-forest)]/10 border border-[var(--brand-forest)]/30" : "bg-rust/10 border border-rust/30"
          }`}>
            {ata.status === "approved" ? (
              <>
                <CheckCircle size={40} weight="fill" className="mx-auto text-[var(--brand-forest)]" />
                <h2 className="font-heading text-2xl mt-2">ÄTA godkänd</h2>
                <p className="text-sm text-text-muted mt-1">
                  Ditt godkännande registrerades {new Date(ata.decided_at).toLocaleString("sv-SE")}.
                </p>
              </>
            ) : (
              <>
                <XCircle size={40} weight="fill" className="mx-auto text-rust" />
                <h2 className="font-heading text-2xl mt-2">ÄTA nekad</h2>
                {ata.decision_reason && (
                  <p className="text-sm text-text-muted mt-2 italic">&ldquo;{ata.decision_reason}&rdquo;</p>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="mt-6 bg-white border border-app rounded-2xl p-6" data-testid="decision-panel">
            <h2 className="font-heading text-xl mb-1">Ditt beslut</h2>
            <p className="text-sm text-text-muted mb-4">
              Genom att klicka <strong>Godkänn</strong> bekräftar du att arbetet enligt ovan får utföras
              till angivet belopp. IP-adress och tidsstämpel loggas för spårbarhet.
            </p>

            <div className="flex flex-col md:flex-row gap-3">
              <button
                data-testid="approve-button"
                disabled={submitting}
                onClick={() => submitDecision("approved")}
                className="tk-btn-primary flex-1 inline-flex items-center justify-center gap-2"
              >
                <CheckCircle size={18} weight="fill" /> Godkänn ÄTA
              </button>
              <button
                data-testid="show-reject-button"
                disabled={submitting}
                onClick={() => setDecision("rejected")}
                className="tk-btn-outline flex-1 inline-flex items-center justify-center gap-2 text-rust"
              >
                <XCircle size={18} /> Neka
              </button>
            </div>

            {decision === "rejected" && (
              <div className="mt-4 space-y-2" data-testid="reject-form">
                <label className="text-xs text-text-muted">Skäl (obligatoriskt)</label>
                <textarea
                  data-testid="reject-reason-input"
                  rows={3}
                  maxLength={500}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="tk-input w-full"
                />
                <button
                  data-testid="confirm-reject-button"
                  disabled={submitting || !rejectReason.trim()}
                  onClick={() => submitDecision("rejected")}
                  className="tk-btn-primary bg-rust hover:bg-rust/90"
                >
                  {submitting ? "Skickar…" : "Bekräfta nekande"}
                </button>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-xs text-text-muted mt-6">
          Digital signering via Tidsrapporten · IP och tidsstämpel loggas för spårbarhet
        </p>
      </div>
    </div>
  );
}
